import { useEffect, useRef } from 'react';
import { Alert, AlertTitle, Box } from '@mui/material';

interface Props {
    /** Formik's `errors`, already narrowed to the fields the form renders. */
    errors: Record<string, string | undefined>;
    /** Formik's `submitCount`. The summary appears only after a submit attempt. */
    submitCount: number;
}

/**
 * The errors a user could not have seen inline, gathered after a failed submit.
 *
 * A screen-reader user who tabs to the button and presses it gets no signal from red borders
 * further up the page, so this is a live region that takes focus. This is also why the submit
 * button is never `disabled`: a disabled button leaves the tab order entirely, so there is nothing
 * to press and nothing to explain why.
 */
export const FormErrorSummary = ({ errors, submitCount }: Props) => {
    const ref = useRef<HTMLDivElement>(null);
    const messages = Object.entries(errors).filter(([, message]) => Boolean(message));
    const show = submitCount > 0 && messages.length > 0;

    useEffect(() => {
        if (show) ref.current?.focus();
        // Re-focus on each failed attempt, not on every keystroke that changes `errors`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitCount]);

    if (!show) return null;

    return (
        <Alert
            severity="error"
            variant="outlined"
            ref={ref}
            tabIndex={-1}
            role="alert"
            sx={{ mb: 1 }}
        >
            <AlertTitle>
                {messages.length === 1
                    ? 'One field needs your attention'
                    : `${messages.length} fields need your attention`}
            </AlertTitle>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {messages.map(([field, message]) => (
                    <li key={field}>{message}</li>
                ))}
            </Box>
        </Alert>
    );
};

export default FormErrorSummary;
